'use client';

import React, { useMemo } from 'react';
import { tv } from 'tailwind-variants';
import ButtonWrapper from 'helpers/SitecoreWrappers/ButtonWrapper/ButtonWrapper';
import type { Section } from '../../types';
import { useUIText } from '../../hooks/useUIText';

interface SectionFooterProps {
  visibleSections: Section[];
  currentSectionIndex: number; // Index in visible sections array
  totalSections: number; // Total number of visible sections
  onStart: () => void; // Start the first incomplete section
  onBack: () => void; // Navigate to previous section
  onNext: () => void; // Navigate to next section
  onFinish: () => void; // Finish the form
  canGoBack: boolean; // Whether back button should be enabled
  canGoNext: boolean; // Whether next button should be enabled
  canStart: boolean; // Whether start button should be enabled
  allSectionsCompleted: boolean; // Whether all sections are completed
  isFirstSectionCompleted: boolean; // Whether the first section is completed
  backStepId?: string; // Static step ID to navigate to when isFirstSection is true
  onSectionClick?: (stepId: string) => void; // Handler to navigate to a step ID
}

export const SectionFooter: React.FC<SectionFooterProps> = ({
  visibleSections,
  currentSectionIndex,
  totalSections,
  onStart,
  onNext,
  onFinish,
  canGoNext,
  canStart,
  allSectionsCompleted,
  isFirstSectionCompleted,
  onBack,
  backStepId,
  onSectionClick,
}) => {
  const currentStep = currentSectionIndex + 1;
  const isFirstSection = currentSectionIndex === 0;
  const isLastSection = currentSectionIndex >= totalSections - 1;
  const progressPercentage = useMemo(() => {
    if (totalSections === 0) return 0;
    if (allSectionsCompleted) return 100;
    return Math.round((currentStep / totalSections) * 100);
  }, [currentStep, totalSections, allSectionsCompleted]);
  const progressWidthStyle = useMemo(() => {
    // Calculate width: (100vw - 10%) * progressPercentage / 100
    // This maintains the same base width calculation as the existing style
    const baseWidthPercent = 100; // 100vw - 10% = 90% of viewport
    const progressWidthPercent = (baseWidthPercent * progressPercentage) / 100;
    return `calc(100vw * ${progressWidthPercent} / 100)`;
  }, [progressPercentage]);

  const handleBackClick = () => {
    if (isFirstSection && backStepId && onSectionClick) {
      // Use static step ID when on first section
      onSectionClick(backStepId);
    } else {
      // Use regular back handler
      onBack();
    }
  };

  const { getButtonText, getProgressText } = useUIText();
  const {
    footerContainer,
    footerContent,
    buttonsContainer,
    progressSection,
    stepText: stepTextClass,
    timeText,
    buttonBase,
  } = TAILWIND_VARIANTS();

  const stepTextValue = useMemo(() => {
    const template = getProgressText('stepTemplate', 'Step {current} of {total}');
    return template
      .replace('{current}', String(allSectionsCompleted ? totalSections : currentStep))
      .replace('{total}', String(totalSections));
  }, [allSectionsCompleted, totalSections, currentStep, getProgressText]);

  const progressTimeRemainingKey = `${visibleSections[currentSectionIndex < totalSections - 1 ? currentSectionIndex + 1 : currentSectionIndex].id}.timeRemaining`;
  const timeTextValue = getProgressText(
    progressTimeRemainingKey,
    `Approximate ${(totalSections - currentSectionIndex) * 2} minutes remaining`
  );

  return (
    <div
      className={footerContainer()}
      style={
        {
          '--progress-bar-width': progressWidthStyle,
        } as React.CSSProperties & { '--progress-bar-width': string }
      }
    >
      <style>{`
      div::after {
        content: '';
        height: 5px;
        display: block;
        z-index: 10;
        position: absolute;
        top: -5px;
        left: calc(-50vw + 50%);
        border-top: 5px solid var(--color-core-color-4-SkyBlue);
        width: var(--progress-bar-width);
      }
    `}</style>
      <div className={footerContent()}>
        <div className={buttonsContainer()}>
          {/* Progress Indicator */}
          <div className={progressSection()}>
            <span className={stepTextClass()}>{stepTextValue}</span>
            {visibleSections[currentSectionIndex] && (
              <span className={timeText()}>{timeTextValue}</span>
            )}
          </div>

          {/* Navigation Buttons */}
          <div className={buttonsContainer()}>
            {isFirstSection && (
              <ButtonWrapper
                type="button"
                text={getButtonText('back', 'Back')}
                ctaComponentClass="default"
                ctaVariant="secondaryInverse"
                onClick={handleBackClick}
                className={buttonBase()}
              />
            )}
            {isFirstSection && canStart && !allSectionsCompleted && (
              <ButtonWrapper
                type="button"
                text={getButtonText('start', 'Start')}
                ctaComponentClass="default"
                ctaVariant="primary"
                onClick={onStart}
                ctaIconAlignment="right"
                ctaIcon="arrow-right"
                ctaIconSize="xs"
                className={buttonBase()}
              />
            )}

            {/* Next Button - Show when not on last section and not all sections are completed */}
            {!isLastSection &&
              (!isFirstSection || (isFirstSection && isFirstSectionCompleted)) &&
              canGoNext &&
              !allSectionsCompleted && (
                <ButtonWrapper
                  type="button"
                  text={getButtonText('nextSection', 'Next Section')}
                  ctaComponentClass="default"
                  ctaVariant="primary"
                  onClick={onNext}
                  ctaIconAlignment="right"
                  ctaIcon="arrow-right"
                  ctaIconSize="xs"
                  className={buttonBase()}
                />
              )}
            {/* Finish Button - Show when not on last section */}
            {allSectionsCompleted && (
              <ButtonWrapper
                type="button"
                text={getButtonText('finish', 'Finish')}
                ctaComponentClass="default"
                ctaVariant="primary"
                onClick={onFinish}
                ctaIconAlignment="right"
                ctaIcon="arrow-right"
                ctaIconSize="xs"
                className={buttonBase()}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const TAILWIND_VARIANTS = tv({
  slots: {
    footerContainer: [
      'sticky',
      'bottom-0',
      'left-0',
      'right-0',
      'w-full',
      'bg-color-bg-dark',
      'z-1',
      'before:block',
      'before:h-[calc(100%_+_5px)]',
      'before:z-[-1]',
      'before:absolute',
      'before:bg-color-bg-dark',
      'before:top-[-5px]',
      'before:left-[calc(-50vw+50%)]',
      'before:right-[calc(-50vw+50%)]',
      'before:border-t-[5px]',
      'before:border-color-grayscale-200',
    ],
    footerContent: ['w-full', 'py-spacing-spacing-24'],
    buttonsContainer: [
      'w-full',
      'flex',
      'flex-row',
      'items-center',
      'justify-end',
      'gap-spacing-spacing-16',
    ],
    progressSection: ['w-full', 'flex', 'flex-col'],
    stepText: [
      'font-typography-font-family-body',
      'text-typography-font-size-body-large',
      'leading-typography-line-height-body-large',
      'text-color-grayscale-white',
      'font-bold',
    ],
    timeText: [
      'hidden md:block',
      'font-typography-font-family-body',
      'text-typography-font-size-body-medium',
      'leading-typography-line-height-body-medium',
      'text-color-grayscale-white',
      'hidden',
      'md:block',
    ],
    buttonBase: ['w-fit', 'leading-[100%]', 'md:leading-[100%]', 'lg:leading-[100%]', 'h-12'],
  },
});
